import React, { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import MuiChip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import MuiPopover, { PopoverOrigin } from '@mui/material/Popover';
import { isNotNull } from '@yi/core';
import { i18n_none } from '@yi/i18n/phrases/i18n_none';
import { i18n_props } from '@yi/i18n/phrases/i18n_props';

import { greyOverlay, black87, moreInfoPopoverZIndex, veryLightGrey, white87 } from '~/styles';

import { useLocale } from './LocaleProvider';

type Props = {
  isAudio: boolean;
  description?: string;
  open: boolean;
  props?: Array<{ name: string } | null> | null;
  handleDismiss(): void;
};

const Description = styled.div`
  color: ${white87};
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.75;
  letter-spacing: 0.5px;
  padding: 30px 0px;
`;

export const PropsLabel = styled.div`
  display: inline;
  margin-right: 14px;
  font-weight: 600;
  font-size: 16px;
`;

const Popover = styled(MuiPopover)`
  .MuiPopover-paper {
    border-radius: 0;
    background-color: ${greyOverlay};
  }
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiPopover>>>;

const ClassProp = styled(MuiChip)`
  background-color: ${veryLightGrey};
  font-weight: 200;
  line-height: 1.43;
  letter-spacing: 0.25px;
  color: ${black87};
  margin: 3px;
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiChip>>>;

function getContainer() {
  /*
   * we need to check document for existence here, not the window.
   * otherwise we get error during SSR, when we access the document
   */
  const isSSR = typeof document === 'undefined';
  if (isSSR) {
    return null;
  }
  return document.fullscreenElement;
}

export const MoreInfoPopover: React.FC<PropsWithChildren<Props>> = ({
  open,
  description,
  handleDismiss,
  isAudio,
  props,
}) => {
  const { locale } = useLocale();

  const anchorOrigin: PopoverOrigin = isAudio
    ? { horizontal: 350, vertical: 'top' }
    : { horizontal: 'right', vertical: 450 };

  const container = getContainer();

  return (
    <Popover
      open={open}
      onClose={handleDismiss}
      style={{ zIndex: moreInfoPopoverZIndex }}
      anchorOrigin={anchorOrigin}
      TransitionComponent={Fade}
      transitionDuration={350}
      container={container}
    >
      <Box m={3} width={400} maxWidth={'90%'} position="relative">
        <CloseIcon
          style={{ position: 'absolute', top: 0, right: 0, color: white87 }}
          onClick={handleDismiss}
        />
        <Description>
          {description}
          {props && (
            <>
              <Divider style={{ marginTop: 4, marginBottom: 8, backgroundColor: white87 }} />
              <PropsLabel>{i18n_props(locale)}</PropsLabel>
              {props.length === 0
                ? i18n_none(locale)
                : props.filter(isNotNull).map(({ name }) => <ClassProp key={name} label={name} />)}
            </>
          )}
        </Description>
      </Box>
    </Popover>
  );
};
